import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";

import { supabase } from "@/lib/supabase";

const LOCAL_API = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i;

function resolveApiBase(): string {
  const configured = (import.meta.env.VITE_API_URL as string | undefined)?.replace(/\/$/, "");
  // In dev, route API through the Vite server so mobile LAN testing works without a LAN IP in env.
  if (import.meta.env.DEV && (!configured || LOCAL_API.test(configured))) return "";
  if (configured) return configured;
  if (import.meta.env.PROD) {
    throw new Error("VITE_API_URL must be set for production builds");
  }
  return "http://localhost:3001";
}

export const BASE = resolveApiBase();

export class ApiError extends Error {
  readonly status: number;
  readonly code?: string;
  readonly params?: Record<string, string | number | boolean>;

  constructor(
    message: string,
    status: number,
    code?: string,
    params?: Record<string, string | number | boolean>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.params = params;
  }
}

export function apiErrorStatus(err: unknown): number | undefined {
  if (err instanceof ApiError) return err.status > 0 ? err.status : undefined;
  if (axios.isAxiosError(err) && err.response?.status != null) return err.response.status;
  if (typeof err === "object" && err !== null && "status" in err) {
    const s = (err as { status: unknown }).status;
    return typeof s === "number" ? s : undefined;
  }
  return undefined;
}

export function apiErrorCode(err: unknown): string | undefined {
  if (err instanceof ApiError) return err.code;
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (typeof data === "object" && data !== null && "code" in data) {
      const c = (data as { code: unknown }).code;
      return typeof c === "string" ? c : undefined;
    }
  }
  if (typeof err === "object" && err !== null && "code" in err) {
    const c = (err as { code: unknown }).code;
    return typeof c === "string" ? c : undefined;
  }
  return undefined;
}

export function apiErrorParams(
  err: unknown,
): Record<string, string | number | boolean> | undefined {
  if (err instanceof ApiError) return err.params;
  if (axios.isAxiosError(err)) {
    const data = err.response?.data;
    if (typeof data === "object" && data !== null && "params" in data) {
      const params = (data as { params: unknown }).params;
      if (typeof params === "object" && params !== null && !Array.isArray(params)) {
        return params as Record<string, string | number | boolean>;
      }
    }
  }
  return undefined;
}

function requestLabel(config: InternalAxiosRequestConfig): string {
  const method = (config.method ?? "get").toUpperCase();
  const path = config.url ?? "";
  return `${method} ${path}`;
}

function parseErrorBody(data: unknown): {
  code?: string;
  params?: Record<string, string | number | boolean>;
  snippet?: string;
} {
  if (data == null) return {};
  if (typeof data === "object" && data !== null) {
    const body = data as { code?: unknown; params?: unknown };
    const code = typeof body.code === "string" ? body.code : undefined;
    let params: Record<string, string | number | boolean> | undefined;
    if (typeof body.params === "object" && body.params !== null && !Array.isArray(body.params)) {
      params = body.params as Record<string, string | number | boolean>;
    }
    const snippet = JSON.stringify(data).slice(0, 400);
    return {
      code,
      params,
      snippet: snippet === "{}" ? undefined : snippet,
    };
  }
  if (typeof data === "string") {
    const trimmed = data.trim();
    if (trimmed === "") return {};
    const snippet = trimmed.slice(0, 400);
    try {
      const parsed = JSON.parse(trimmed) as { code?: unknown; params?: unknown };
      const code = typeof parsed.code === "string" ? parsed.code : undefined;
      let params: Record<string, string | number | boolean> | undefined;
      if (
        typeof parsed.params === "object" &&
        parsed.params !== null &&
        !Array.isArray(parsed.params)
      ) {
        params = parsed.params as Record<string, string | number | boolean>;
      }
      return { code, params, snippet };
    } catch {
      return { snippet };
    }
  }
  const snippet = String(data).slice(0, 400);
  return { snippet: snippet === "" ? undefined : snippet };
}

function toApiError(error: AxiosError, label: string): ApiError {
  const status = error.response?.status ?? 0;
  const { code, params, snippet } = parseErrorBody(error.response?.data);
  const suffix = snippet ? `: ${snippet}` : "";
  const message =
    status > 0 ? `${label} failed: ${status}${suffix}` : `${label} failed: ${error.message}`;
  return new ApiError(message, status > 0 ? status : 0, code, params);
}

/** Prefer `accessTokenHint` from `onAuthStateChange`/`getSession` to avoid stale reads right after login. */
async function buildBearerHeaders(
  accessTokenHint?: string | null,
): Promise<Record<string, string>> {
  if (!supabase) return {};
  if (accessTokenHint) return { Authorization: `Bearer ${accessTokenHint}` };

  const {
    data: { session: initialSession },
  } = await supabase.auth.getSession();
  let session = initialSession;
  if (!session?.access_token) {
    try {
      const { data } = await supabase.auth.refreshSession();
      session = data.session ?? null;
    } catch {
      /* no refresh path */
    }
  }
  const token = session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export const api = axios.create({
  baseURL: BASE,
  headers: {
    Accept: "application/json",
    "X-Version-Web": import.meta.env.VITE_GIT_SHA ?? "unknown",
  },
});

api.interceptors.request.use(async (config) => {
  const auth = await buildBearerHeaders(config.accessTokenHint);
  const token = auth.Authorization;
  if (token) {
    config.headers.set("Authorization", token);
  }
  // Tenant comes from server-side user_active_organizations (or first membership).
  // Do not send a client-cached X-Organization-Id — it drifts across account switches.
  return config;
});

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as InternalAxiosRequestConfig | undefined;
    if (!config) return Promise.reject(error);

    const status = error.response?.status;
    const label = requestLabel(config);

    if (status === 401 && supabase && !config._retry) {
      const hint = config.accessTokenHint ?? null;
      const skipRefresh = typeof hint === "string" && hint.length > 0;
      if (!skipRefresh) {
        config._retry = true;
        await supabase.auth.refreshSession().catch(() => {});
        try {
          return await api.request(config);
        } catch (retryError) {
          if (axios.isAxiosError(retryError) && retryError.response?.status === 401) {
            if (import.meta.env.DEV) {
              const url = `${config.baseURL ?? BASE}${config.url ?? ""}`;
              console.warn(
                `[konstrulog] Still 401 after refresh for ${url}. API base is ${BASE}. ` +
                  "Confirm the backend verifies Supabase JWTs with the JWT secret from the same project as VITE_SUPABASE_URL (Dashboard → Settings → API).",
              );
            }
          }
          if (axios.isAxiosError(retryError)) {
            return Promise.reject(toApiError(retryError, label));
          }
          return Promise.reject(retryError);
        }
      }
      if (import.meta.env.DEV) {
        const url = `${config.baseURL ?? BASE}${config.url ?? ""}`;
        console.warn(
          `[konstrulog] Still 401 after refresh for ${url}. API base is ${BASE}. ` +
            "Confirm the backend verifies Supabase JWTs with the JWT secret from the same project as VITE_SUPABASE_URL (Dashboard → Settings → API).",
        );
      }
    }

    if (axios.isAxiosError(error)) {
      return Promise.reject(toApiError(error, label));
    }
    return Promise.reject(error);
  },
);
